import { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { queryClient } from './lib/queryClient';

// Layouts
import MainLayout from './layouts/MainLayout';
import AuthLayout from './layouts/AuthLayout';

// Auth Pages
import Login from './pages/auth/Login';
import SsoHandoff from './pages/auth/SsoHandoff';
import Register from './pages/auth/Register';
import PasswordReset from './pages/auth/PasswordReset';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import ContactLogin from './pages/auth/ContactLogin';
import ContactChangePassword from './pages/auth/ContactChangePassword';

// Dashboard
import Dashboard from './pages/Dashboard';
import KpiDashboard from './pages/kpi-dashboard/KpiDashboard';
import Bandwidth from './pages/bandwidth/Bandwidth';

// Incident Pages
import IncidentList from './pages/incidents/IncidentList';
import IncidentDetail from './pages/incidents/IncidentDetail';
import IncidentCreate from './pages/incidents/IncidentCreate';

// Problem Pages
import ProblemList from './pages/problems/ProblemList';
import ProblemDetail from './pages/problems/ProblemDetail';
import ProblemCreate from './pages/problems/ProblemCreate';
import ProblemEdit from './pages/problems/ProblemEdit';

// Change Pages
import ChangeList from './pages/changes/ChangeList';
import ChangeDetail from './pages/changes/ChangeDetail';
import ChangeCreate from './pages/changes/ChangeCreate';

// Case Pages
import CaseList from './pages/cases/CaseList';
import CaseDetail from './pages/cases/CaseDetail';
import CaseCreate from './pages/cases/CaseCreate';
import CaseEdit from './pages/cases/CaseEdit';

// Service Request Pages
import ServiceRequestList from './pages/service-requests/ServiceRequestList';
import ServiceRequestDetail from './pages/service-requests/ServiceRequestDetail';
import ServiceRequestCreate from './pages/service-requests/ServiceRequestCreate';
import ServiceRequestEdit from './pages/service-requests/ServiceRequestEdit';

// Tickets Page
import Tickets from './pages/tickets/Tickets';
import TicketDetail from './pages/tickets/TicketDetail';

// Alert Pages
import AlertList from './pages/alerts/AlertList';
import AlertDetail from './pages/alerts/AlertDetail';

// SRE Investigation showcase (demo / live workbench)
import SreInvestigationPage from './pages/sre-investigation/SreInvestigationPage';

// Survey Pages
import SurveyList from './pages/surveys/SurveyList';
import SurveyDetail from './pages/surveys/SurveyDetail';

// Public Survey Page
import PublicSurvey from './pages/public/PublicSurvey';

// Knowledge Pages
import KnowledgeList from './pages/knowledge/KnowledgeList';
import KnowledgeDetail from './pages/knowledge/KnowledgeDetail';
import KnowledgeCreate from './pages/knowledge/KnowledgeCreate';
import KnowledgeEdit from './pages/knowledge/KnowledgeEdit';

// Knowledge Base Pages
import KnowledgeBaseList from './pages/knowledge-base/KnowledgeBaseList';
import KnowledgeBaseForm from './pages/knowledge-base/KnowledgeBaseForm';

// Approval Pages
import MyApprovals from './pages/approvals/MyApprovals';

// Admin Pages
import PermissionsManagement from './pages/admin/PermissionsManagement';
import PipelineVariables from './pages/admin/VariableTemplates';
import SlaConditions from './pages/admin/SlaConditions';
import SlaConditionForm from './pages/admin/SlaConditionForm';
import EmailTemplatesManagement from './pages/admin/EmailTemplatesManagement';
import NotificationRulesManagement from './pages/admin/NotificationRulesManagement';
import NotificationLogsManagement from './pages/admin/NotificationLogsManagement';
import UserNotificationPreferences from './pages/admin/UserNotificationPreferences';
import SurveyTemplatesManagement from './pages/admin/SurveyTemplatesManagement';
import DefaultQuestionsManagement from './pages/admin/DefaultQuestionsManagement';
import SurveyEmailTemplatesManagement from './pages/admin/SurveyEmailTemplatesManagement';


// User Pages
import UserList from './pages/users/UserList';
import UserDetail from './pages/users/UserDetail';
import UserCreate from './pages/users/UserCreate';

// Contact Pages
import ContactList from './pages/contacts/ContactList';
import ContactCreate from './pages/contacts/ContactCreate';
import ContactDetail from './pages/contacts/ContactDetail';
import ContactEdit from './pages/contacts/ContactEdit';

// Error Pages
import NotFound from './pages/NotFound';

// Permission Guard
import PermissionGuard from './components/PermissionGuard';

// Customer Pages
import CustomerList from './pages/customers/CustomerList';
import CustomerDetail from './pages/customers/CustomerDetail';
import CustomerForm from './pages/customers/CustomerForm';

// Groups Pages
import GroupsList from './pages/groups/GroupsList';
import GroupDetail from './pages/groups/GroupDetail';
import GroupCreate from './pages/groups/GroupCreate';

// Time Entry Pages
import TimeEntryList from './pages/time-entries/TimeEntryList';
import TimeEntryCreate from './pages/time-entries/TimeEntryCreate';
import TimeEntryEdit from './pages/time-entries/TimeEntryEdit';

// Billing Pages
import BillingDashboard from './pages/billing/BillingDashboard';

// Environment Pages
import EnvironmentList from './pages/environments/EnvironmentList';
import EnvironmentCreate from './pages/environments/EnvironmentCreate';
import EnvironmentDetail from './pages/environments/EnvironmentDetail';
import EnvironmentEdit from './pages/environments/EnvironmentEdit';
import EnvironmentRequestDetail from './pages/environments/EnvironmentRequestDetail';
import RequestedItemsList from './pages/environments/RequestedItemsList';

// Workflow Pages
import WorkflowList from './pages/workflows/WorkflowList';
import WorkflowEditor from './pages/workflows/WorkflowEditor';

// Offer Pages
import OfferList from './pages/offers/OfferList';
import OfferDetail from './pages/offers/OfferDetail';
import OfferCreate from './pages/offers/OfferCreate';
import OfferEdit from './pages/offers/OfferEdit';

// Engagement Pages
import EngagementList from './pages/engagements/EngagementList';
import EngagementDetail from './pages/engagements/EngagementDetail';
import EngagementCreate from './pages/engagements/EngagementCreate';
import EngagementEdit from './pages/engagements/EngagementEdit';

// Service Plan Pages
import PlanList from './pages/service-plans/PlanList';
import PlanForm from './pages/service-plans/PlanForm';
import PlanDetail from './pages/service-plans/PlanDetail';

// Resource Pages
import ResourceList from './pages/resources/ResourceList';
import ResourceDetail from './pages/resources/ResourceDetail';

// LabTech Logs Pages
import LabtechLogList from './pages/labtech-logs/LabtechLogList';
import AllocationEmployeeSync from './pages/admin/AllocationEmployeeSync';
import ServiceNowIntegrationConfig from './pages/servicenow-integration/ServiceNowIntegrationConfig';
import ServiceNowSyncLogList from './pages/servicenow-integration/ServiceNowSyncLogList';
import CaseSuppressionKeywords from './pages/admin/CaseSuppressionKeywordsPage';
import CabDefinitionsList from './pages/admin/cab-meeting-management/CabDefinitionsList';
import CabDefinitionCreate from './pages/admin/cab-meeting-management/CabDefinitionCreate';
import CabWorkbench from './pages/admin/cab-meeting-management/CabWorkbench';
import CabWorkbenchMeetingDetail from './pages/admin/cab-meeting-management/CabWorkbenchMeetingDetail';
import CabDefinitionDetail from './pages/admin/cab-meeting-management/CabDefinitionDetail';
import ScheduleEntryCreate from './pages/admin/cab-meeting-management/ScheduleEntryCreate';
import ScheduleEntryEdit from './pages/admin/cab-meeting-management/ScheduleEntryEdit';
import CabMeetingDetail from './pages/admin/cab-meeting-management/CabMeetingDetail';
import CabAgendaItemCreate from './pages/admin/cab-meeting-management/CabAgendaItemCreate';
import CabAgendaItemEdit from './pages/admin/cab-meeting-management/CabAgendaItemEdit';
import CabAttendeeEdit from './pages/admin/cab-meeting-management/CabAttendeeEdit';
import MonitoringDashboard from './pages/monitoring/MonitoringDashboard';
import MonitoringConfiguration from './pages/monitoring/MonitoringConfiguration';

// Auth Context
import { AuthProvider } from './contexts/AuthContext';
import PrivateRoute from './components/PrivateRoute';
import ContactBlockedRoute from './components/ContactBlockedRoute';
import MsalProviderWrapper from './components/MsalProviderWrapper';
import RootRedirect from './components/RootRedirect';
import SessionManager from './components/SessionManager';

// Conditionally import ReactQueryDevtools in development only to avoid loading unnecessary chunks in production
const ReactQueryDevtools =
  process.env.NODE_ENV === 'development'
    ? lazy(() =>
        import('@tanstack/react-query-devtools').then((mod) => ({
          default: mod.ReactQueryDevtools,
        }))
      )
    : () => null;

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <MsalProviderWrapper>
          <AuthProvider>
            <SessionManager>
              <Toaster position="top-right" />
              <Routes>
            {/* Public routes - no authentication required */}
            <Route path="/public/survey/:surveyId" element={<PublicSurvey />} />
            {/* Pulse-SRE SSO handoff: bounces through Pulse login if needed */}
            <Route path="/sso/handoff" element={<SsoHandoff />} />

            {/* Auth routes */}
            <Route element={<AuthLayout />}>
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/password-reset" element={<PasswordReset />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/contact/login" element={<ContactLogin />} />
              <Route path="/contact/change-password" element={<ContactChangePassword />} />
            </Route>

            {/* Protected routes */}
            <Route element={<PrivateRoute><MainLayout /></PrivateRoute>}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route
                path="/kpi-dashboard"
                element={
                  <PermissionGuard resource="kpi_dashboard">
                    <KpiDashboard />
                  </PermissionGuard>
                }
              />
              <Route
                path="/bandwidth"
                element={
                  <PermissionGuard resource="bandwidth">
                    <Bandwidth />
                  </PermissionGuard>
                }
              />

              {/* Incident routes - blocked for contact users */}
              <Route path="/incidents" element={<ContactBlockedRoute><IncidentList /></ContactBlockedRoute>} />
              <Route path="/incidents/new" element={<ContactBlockedRoute><IncidentCreate /></ContactBlockedRoute>} />
              <Route path="/incidents/:id" element={<ContactBlockedRoute><IncidentDetail /></ContactBlockedRoute>} />

              {/* Problem routes - blocked for contact users */}
              <Route path="/problems" element={<ContactBlockedRoute><ProblemList /></ContactBlockedRoute>} />
              <Route path="/problems/new" element={<ContactBlockedRoute><ProblemCreate /></ContactBlockedRoute>} />
              <Route path="/problems/:id/edit" element={<ContactBlockedRoute><ProblemEdit /></ContactBlockedRoute>} />
              <Route path="/problems/:id" element={<ContactBlockedRoute><ProblemDetail /></ContactBlockedRoute>} />

              {/* Change routes - blocked for contact users */}
              <Route path="/changes" element={<ContactBlockedRoute><ChangeList /></ContactBlockedRoute>} />
              <Route path="/changes/new" element={<ContactBlockedRoute><ChangeCreate /></ContactBlockedRoute>} />
              <Route path="/changes/:id" element={<ContactBlockedRoute><ChangeDetail /></ContactBlockedRoute>} />

              {/* Service Request routes - blocked for contact users */}
              <Route path="/service-requests" element={<ContactBlockedRoute><ServiceRequestList /></ContactBlockedRoute>} />
              <Route path="/service-requests/new" element={<ContactBlockedRoute><ServiceRequestCreate /></ContactBlockedRoute>} />
              <Route path="/service-requests/:id" element={<ContactBlockedRoute><ServiceRequestDetail /></ContactBlockedRoute>} />
              <Route path="/service-requests/:id/edit" element={<ContactBlockedRoute><ServiceRequestEdit /></ContactBlockedRoute>} />

              {/* Case routes - list and new accessible, detail/edit blocked for contact users */}
              <Route path="/cases" element={<CaseList />} />
              <Route path="/cases/new" element={<CaseCreate />} />
              <Route path="/cases/:id/edit" element={<ContactBlockedRoute><CaseEdit /></ContactBlockedRoute>} />
              <Route path="/cases/:id" element={<ContactBlockedRoute><CaseDetail /></ContactBlockedRoute>} />

              {/* Tickets routes */}
              <Route path="/tickets" element={<Tickets />} />
              <Route path="/tickets/:id" element={<TicketDetail />} />
              
              {/* Alert routes */}
              <Route path="/alerts" element={<AlertList />} />
              <Route path="/alerts/:id" element={<AlertDetail />} />

              {/* SRE Investigation showcase — report + CTA only; skills/use-cases live in Pulse-SRE */}
              <Route path="/sre/investigate" element={<SreInvestigationPage />} />
              

              {/* Survey routes */}
              <Route path="/surveys" element={<SurveyList />} />
              <Route path="/surveys/:id" element={<SurveyDetail />} />
              
              {/* Knowledge routes */}
              <Route path="/knowledge" element={<KnowledgeList />} />
              <Route path="/knowledge/new" element={<KnowledgeCreate />} />
              <Route path="/knowledge/:id" element={<KnowledgeDetail />} />
              <Route path="/knowledge/:id/edit" element={<KnowledgeEdit />} />

              {/* Knowledge Base routes */}
              <Route path="/knowledge-base" element={<KnowledgeBaseList />} />
              <Route path="/knowledge-base/new" element={<KnowledgeBaseForm />} />
              <Route path="/knowledge-base/:id/edit" element={<KnowledgeBaseForm />} />

              {/* Approval routes */}
              <Route path="/approvals" element={<MyApprovals />} />

              {/* Domain routes */}
              
              
              {/* User routes */}
              <Route path="/users" element={<UserList />} />
              <Route path="/users/new" element={<UserCreate />} />
              <Route path="/users/:id" element={<UserDetail />} />

              {/* Contact routes */}
              <Route path="/contacts" element={<ContactList />} />
              <Route path="/contacts/new" element={<ContactCreate />} />
              <Route path="/contacts/:id" element={<ContactDetail />} />
              <Route path="/contacts/:id/edit" element={<ContactEdit />} />


              {/* Customer routes */}
              <Route path="/customers" element={<CustomerList />} />
              <Route path="/customers/new" element={<CustomerForm />} />
              <Route path="/customers/:id" element={<CustomerDetail />} />
              <Route path="/customers/:id/edit" element={<CustomerForm />} />
              
              {/* Admin routes */}
              <Route path="/admin/permissions" element={
                <PermissionGuard resource="permissions">
                  <PermissionsManagement />
                </PermissionGuard>
              } />
              <Route path="/admin/variables" element={<PipelineVariables />} />
              <Route path="/admin/sla-conditions" element={<SlaConditions />} />
              <Route path="/admin/sla-conditions/new" element={<SlaConditionForm />} />
              <Route path="/admin/sla-conditions/:id/edit" element={<SlaConditionForm />} />
              <Route path="/admin/email-templates" element={
                <PermissionGuard resource="email_template">
                  <EmailTemplatesManagement />
                </PermissionGuard>
              } />
              <Route path="/admin/notification-rules" element={
                <PermissionGuard resource="notification_rule">
                  <NotificationRulesManagement />
                </PermissionGuard>
              } />
              <Route path="/admin/notification-logs" element={
                <PermissionGuard resource="notification_log">
                  <NotificationLogsManagement />
                </PermissionGuard>
              } />
              <Route path="/admin/notification-preferences" element={<UserNotificationPreferences />} />
              <Route path="/admin/labtech-logs" element={
                <PermissionGuard resource="integration_logs">
                  <LabtechLogList />
                </PermissionGuard>
              } />
              <Route path="/admin/allocation-employees" element={
                <PermissionGuard roles={['admin', 'super_admin']}>
                  <AllocationEmployeeSync />
                </PermissionGuard>
              } />
              <Route path="/admin/servicenow-integration" element={
                <PermissionGuard resource="integration_logs">
                  <ServiceNowIntegrationConfig />
                </PermissionGuard>
              } />
              <Route path="/admin/servicenow-logs" element={
                <PermissionGuard resource="integration_logs">
                  <ServiceNowSyncLogList />
                </PermissionGuard>
              } />
              <Route path="/admin/survey-templates" element={
                <PermissionGuard resource="survey_templates">
                  <SurveyTemplatesManagement />
                </PermissionGuard>
              } />
              <Route path="/admin/default-questions" element={
                <PermissionGuard resource="survey_templates">
                  <DefaultQuestionsManagement />
                </PermissionGuard>
              } />
              <Route path="/admin/survey-email-templates" element={
                <PermissionGuard resource="survey_templates">
                  <SurveyEmailTemplatesManagement />
                </PermissionGuard>
              } />
              <Route path="/admin/case-suppression" element={
                <PermissionGuard resource="case_suppression">
                  <CaseSuppressionKeywords />
                </PermissionGuard>
              } />
              <Route path="/admin/cab-meeting-management/cab-definitions" element={
                <PermissionGuard resource="cab_definitions">
                  <CabDefinitionsList />
                </PermissionGuard>
              } />
              <Route path="/admin/cab-meeting-management/cab-definitions/new" element={
                <PermissionGuard resource="cab_definitions" action="create">
                  <CabDefinitionCreate />
                </PermissionGuard>
              } />
              <Route path="/admin/cab-meeting-management/cab-definitions/:id/schedule-entry/new" element={
                <PermissionGuard resource="cab_definitions" action="create">
                  <ScheduleEntryCreate />
                </PermissionGuard>
              } />
              <Route path="/admin/cab-meeting-management/cab-definitions/:id/schedule-entry/:entryId/edit" element={
                <PermissionGuard resource="cab_definitions" action="update">
                  <ScheduleEntryEdit />
                </PermissionGuard>
              } />
              <Route path="/admin/cab-meeting-management/cab-meetings/:meetingId/agenda-items/new" element={
                <PermissionGuard resource="cab_definitions" action="create">
                  <CabAgendaItemCreate />
                </PermissionGuard>
              } />
              <Route path="/admin/cab-meeting-management/cab-meetings/:meetingId/agenda-items/:agendaItemId/edit" element={
                <PermissionGuard resource="cab_definitions" action="update">
                  <CabAgendaItemEdit />
                </PermissionGuard>
              } />
              <Route path="/admin/cab-meeting-management/cab-meetings/:meetingId/attendees/:attendeeId/edit" element={
                <PermissionGuard resource="cab_definitions" action="update">
                  <CabAttendeeEdit />
                </PermissionGuard>
              } />
              <Route path="/admin/cab-meeting-management/cab-meetings/:meetingId" element={
                <PermissionGuard resource="cab_definitions">
                  <CabMeetingDetail />
                </PermissionGuard>
              } />
              <Route path="/admin/cab-meeting-management/cab-definitions/:id" element={
                <PermissionGuard resource="cab_definitions">
                  <CabDefinitionDetail />
                </PermissionGuard>
              } />
              <Route path="/admin/cab-meeting-management/cab-workbench/meetings/:meetingId" element={
                <PermissionGuard resource="cab_workbench">
                  <CabWorkbenchMeetingDetail />
                </PermissionGuard>
              } />
              <Route path="/admin/cab-meeting-management/cab-workbench" element={
                <PermissionGuard resource="cab_workbench">
                  <CabWorkbench />
                </PermissionGuard>
              } />

              {/* Groups routes */}
              <Route path="/groups" element={<GroupsList />} />
              <Route path="/groups/new" element={<GroupCreate />} />
              <Route path="/groups/:id" element={<GroupDetail />} />
              
              {/* Environment routes */}
              <Route path="/environments" element={<EnvironmentList />} />
              <Route path="/environments/new" element={<EnvironmentCreate />} />
              <Route path="/environments/:id" element={<EnvironmentDetail />} />
              <Route path="/environments/:id/edit" element={<EnvironmentEdit />} />
              <Route path="/environment-requests" element={<RequestedItemsList />} />
              <Route path="/environment-requests/:id" element={<EnvironmentRequestDetail />} />
              
              {/* Workflow routes */}
              <Route path="/workflows" element={<WorkflowList />} />
              <Route path="/workflows/new" element={<WorkflowEditor />} />
              <Route path="/workflows/:id" element={<WorkflowEditor />} />
              
              {/* Offer routes */}
              <Route path="/offers" element={<OfferList />} />
              <Route path="/offers/new" element={<OfferCreate />} />
              <Route path="/offers/:id" element={<OfferDetail />} />
              <Route path="/offers/:id/edit" element={<OfferEdit />} />
              
              {/* Engagement routes */}
              <Route path="/engagements" element={<EngagementList />} />
              <Route path="/engagements/create" element={<EngagementCreate />} />
              <Route path="/engagements/:id" element={<EngagementDetail />} />
              <Route path="/engagements/:id/edit" element={<EngagementEdit />} />
              
              {/* Service Plan routes */}
              <Route path="/service-plans" element={<PlanList />} />
              <Route path="/service-plans/create" element={<PlanForm />} />
              <Route path="/service-plans/:id" element={<PlanDetail />} />
              <Route path="/service-plans/:id/edit" element={<PlanForm />} />
              
              {/* Resource routes */}
              <Route path="/resources" element={<ResourceList />} />
              <Route path="/resources/:id" element={<ResourceDetail />} />

              {/* Billing routes */}
              <Route path="/billing" element={
                <PermissionGuard resource="billing">
                  <BillingDashboard />
                </PermissionGuard>
              } />

              {/* Time Entry routes */}
              <Route path="/time-entries" element={<TimeEntryList />} />
              <Route path="/time-entries/new" element={<TimeEntryCreate />} />
              <Route path="/time-entries/:id/edit" element={<TimeEntryEdit />} />

              {/* Monitoring route */}
              <Route path="/monitoring" element={<MonitoringDashboard />} />
              <Route path="/configuration" element={<MonitoringConfiguration />} />

              {/* 404 - Catch all unmatched routes */}
              <Route path="*" element={<NotFound />} />
            </Route>

            {/* Default redirect - routes contacts to contact dashboard */}
            <Route path="/" element={<RootRedirect />} />
              </Routes>
            </SessionManager>
          </AuthProvider>
        </MsalProviderWrapper>
      </Router>
      {process.env.NODE_ENV === 'development' && (
        <Suspense fallback={null}>
          <ReactQueryDevtools initialIsOpen={false} />
        </Suspense>
      )}
    </QueryClientProvider>
  );
}

export default App;
